/**
 * 晋级动画组件（屏 5）
 *
 * 身份徽章升级动画 + "深蓝计划指挥部通知"文案。
 * 触发条件：markLevelCompleted 返回 phaseUnlocked=true。
 *
 * 设计约束：Ardot v2.0 战术美学（锐角/描边/monospace/逐字显现）
 */

export interface RankUpData {
  /** 旧身份名称 */
  fromRank: string;
  /** 新身份名称 */
  toRank: string;
  /** 新身份代号 */
  toCodename: string;
  /** 晋级通知文案 */
  message: string;
  /** 新身份颜色 */
  color: string;
}

/**
 * 在指定容器中展示晋级动画
 */
export function showRankUpAnimation(container: HTMLElement, data: RankUpData): void {
  const wrapper = document.createElement("div");
  wrapper.className = "rank-up-animation";

  wrapper.innerHTML = `
    <div class="rank-up-overlay">
      <!-- 身份徽章 -->
      <div class="rank-up-badge" style="border-color: ${data.color}; color: ${data.color};">
        <div class="rank-up-badge-icon">◈</div>
        <div class="rank-up-badge-name">${escapeHtml(data.toRank)}</div>
        <div class="rank-up-badge-code">${escapeHtml(data.toCodename)}</div>
      </div>

      <!-- 箭头过渡 -->
      <div class="rank-up-transition">
        <span class="rank-up-from">${escapeHtml(data.fromRank)}</span>
        <span class="rank-up-arrow">→</span>
        <span class="rank-up-to" style="color: ${data.color};">${escapeHtml(data.toRank)}</span>
      </div>

      <!-- 通知文案 -->
      <div class="rank-up-message">
        <span class="rank-up-label">深蓝计划指挥部 · 通知</span>
        <p class="rank-up-text">${escapeHtml(data.message)}</p>
      </div>
    </div>
  `;

  container.appendChild(wrapper);

  // 动画播放后自动移除（8 秒后淡出）
  setTimeout(() => {
    wrapper.classList.add("rank-up-animation--fade-out");
    setTimeout(() => wrapper.remove(), 600);
  }, 8000);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

/**
 * 从 rank-system.json 数据中匹配晋级数据
 */
export function getRankUpData(fromPhase: number, toPhase: number): RankUpData | null {
  // 本地硬编码映射（避免异步加载 json 的复杂性）
  const RANK_MAP: Record<number, { name: string; codename: string; color: string; promotionMessage: string }> = {
    0: {
      name: "训练生",
      codename: "TRAINEE",
      color: "#8899AA",
      promotionMessage: "",
    },
    1: {
      name: "战术新星",
      codename: "NOVA",
      color: "#4EC9B0",
      promotionMessage: "深蓝计划指挥部通知：推演官，你的权限已升级至「战术新星」。你已掌握基础指令——现在，推演将为你展开真正的对抗。",
    },
    2: {
      name: "对抗专家",
      codename: "ADEPT",
      color: "#569CD6",
      promotionMessage: "深蓝计划指挥部通知：推演官，你的权限已升级至「对抗专家」。你对掩体与火力间隔的理解已臻熟练——是时候在多条件博弈中展现判断力了。",
    },
    3: {
      name: "推演架构师",
      codename: "ARCHITECT",
      color: "#C586C0",
      promotionMessage: "深蓝计划指挥部通知：推演官，你的权限已升级至「推演架构师」。从空白界面构建推演方案的能力，标志着你的水平已达到专业级。你的 Agent 是你意志的延伸。",
    },
    4: {
      name: "推演架构师",
      codename: "ARCHITECT",
      color: "#C586C0",
      promotionMessage: "",
    },
  };

  const from = RANK_MAP[fromPhase];
  const to = RANK_MAP[toPhase];
  if (!to || to.promotionMessage === "") return null;

  return {
    fromRank: from?.name ?? "未知",
    toRank: to.name,
    toCodename: to.codename,
    message: to.promotionMessage,
    color: to.color,
  };
}
